import { User } from "@/lib/api/common";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import * as api from '@/lib/api';

export const ACCESS_TOKEN_STORAGE_KEY = 'sleep-scoring-auth-token';

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User|null>(null);
    const isAuthenticated = computed(() => !!user.value);
    const isAdmin = computed(() => user.value?.role === 'admin');

    /**
     * Logs in a user with the provided email and password.
     * 
     * @param email - The user's email.
     * @param password - The user's password.
     */
    const login = async (email: string, password: string) => {
        const res = await api.auth.login({ email, password })
        user.value = res.user;
        // save token in local storage
        window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, res.token);
        // return response
        return res;
    }

    /**
     * Logs the user out and clears the session.
     */
    const logout = async () => {
        await api.auth.logout();
        user.value = null;
        // remove token from local storage
        window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }

    /**
     * Re-authenticates the user using the token stored in local storage.
     * @returns A promise that resolves to true if re-authentication is successful, or false otherwise.
     */
    const reAuth = async (): Promise<User|null> => {
        // check if there is a token in local storage
        const token = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
        if (token) {
            // try to re-authenticate the user using the token in local storage
            try {
                const res = await api.auth.refresh();
                user.value = res.user;
                // update local storage with the refreshed token 
                window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, res.token);
                return res.user;
            } catch(err) {
                return null;
            }
        }
        return null;
    }

    const reset = () => {
        user.value = null;
        window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    } 

    const setUser = (u: User) => {
        user.value = u;
    }

    const setSession = (token: string, user: User) => {
        window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
        setUser(user);
    }

    return {
        user,
        isAuthenticated,
        isAdmin,
        // actions
        login,
        logout,
        reAuth,
        reset,
        setUser,
        setSession,
    }
})